import React, { useCallback, useMemo, useState } from 'react';
import { View, useWindowDimensions } from 'react-native';
import PromptCard, { PromptCardPlaceholder } from '@/components/Prompts/PromptCard';
import useResponsiveness from '@/hooks/useResponsiveness';
import { getPromptGridCardWidthPx, getPromptGridColumnCount } from '@/components/Prompts/promptGridLayout';

function PromptGrid({
  prompts,
  isLoading = false,
  placeholderCount = 9,
  onPromptPress,
  style,
  /** When set, caps the pre-layout width estimate (detail pages sit in ~800px columns). */
  contentMaxWidth = null,
  /** Override max columns (default 3; compact related grids use 5). */
  maxColumns = null,
  /** Override minimum card width for denser grids. */
  minCardWidth = null,
}) {
  const { isXS } = useResponsiveness();
  const { width: windowWidth } = useWindowDimensions();
  const [measuredWidth, setMeasuredWidth] = useState(null);

  const gap = isXS ? 10 : 12;

  const onGridLayout = useCallback((e) => {
    const w = e?.nativeEvent?.layout?.width;
    if (typeof w === 'number' && w > 0) {
      setMeasuredWidth(Math.round(w));
    }
  }, []);

  const estimatedWidth = contentMaxWidth != null && contentMaxWidth > 0
    ? Math.min(windowWidth, contentMaxWidth)
    : windowWidth;
  const bodyWidth = measuredWidth && measuredWidth > 0 ? measuredWidth : estimatedWidth;
  const gridOptions = useMemo(() => ({
    ...(maxColumns != null ? { maxColumns } : {}),
    ...(minCardWidth != null ? { minCardWidth } : {}),
  }), [maxColumns, minCardWidth]);

  const columnCount = useMemo(
    () => getPromptGridColumnCount(bodyWidth, gap, gridOptions),
    [bodyWidth, gap, gridOptions],
  );
  const cardWidthPx = useMemo(
    () => getPromptGridCardWidthPx(bodyWidth, columnCount, gap),
    [bodyWidth, columnCount, gap],
  );

  const gridStyle = useMemo(() => ({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap,
    width: '100%',
  }), [gap]);

  if (isLoading) {
    return (
      <View style={[gridStyle, style]} onLayout={onGridLayout}>
        {[...Array(placeholderCount)].map((_, index) => (
          <PromptCardPlaceholder key={index} measuredCardWidth={cardWidthPx} />
        ))}
      </View>
    );
  }

  return (
    <View style={[gridStyle, style]} onLayout={onGridLayout}>
      {Array.isArray(prompts) && prompts.map(prompt => (
        <PromptCard
          key={prompt.id || prompt._id || prompt.slug}
          prompt={prompt}
          onPress={onPromptPress}
          measuredCardWidth={cardWidthPx}
        />
      ))}
    </View>
  );
}

export default React.memo(PromptGrid);
